
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useBandDisagreementActions = (refetch: () => void, activeYear: string) => {
  const { toast } = useToast();

  const checkIfBandAccepted = async (employeeId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('performance_bands')
        .select('status')
        .eq('employee_id', employeeId)
        .eq('year', activeYear)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking band acceptance status:', error);
        return false;
      }

      return data?.status === 'Final Band Accepted';
    } catch (error) {
      console.error('Error checking band acceptance status:', error);
      return false;
    }
  };

  const handleSaveAndClose = async (
    selectedRecord: any,
    isSupervisor: boolean,
    resolutionOutcome: string,
    resolutionNotes: string,
    setSelectedRecord: (record: any) => void,
    isUnitHead?: boolean
  ) => {
    if (!selectedRecord || (!isSupervisor && !isUnitHead)) return;

    try {
      // Check if the employee has already accepted their band
      const isAccepted = await checkIfBandAccepted(selectedRecord.user_id);
      if (isAccepted) {
        toast({
          title: "Cannot Update",
          description: "This employee has already accepted their final performance band and it cannot be modified.",
          variant: "destructive",
        });
        return;
      }

      // Extract only the letter from the resolution outcome and validate it
      const bandLetter = resolutionOutcome.replace('Band ', '').trim().toUpperCase();
      
      // Validate that the band letter is one of the allowed values
      if (!['A', 'B', 'C', 'D'].includes(bandLetter)) {
        console.error('Invalid band letter:', bandLetter);
        toast({
          title: "Error",
          description: "Invalid performance band value. Please select a valid band (A, B, C, or D).",
          variant: "destructive",
        });
        return;
      }
      
      // Update band_disagreements table - only save the resolution notes and status
      const { error: bandDisagreementError } = await supabase
        .from('band_disagreements')
        .update({
          resolution_outcome: bandLetter,
          resolution_notes: resolutionNotes,
          status: 'Band disagreement resolution completed',
          triage_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRecord.id);

      if (bandDisagreementError) {
        console.error('Error saving band disagreement:', bandDisagreementError);
        toast({
          title: "Error",
          description: "Failed to save changes. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Update performance_bands table - update the final_banding, supervisor comments and status
      const { error: performanceBandError } = await supabase
        .from('performance_bands')
        .update({
          final_banding: bandLetter,
          supervisor_banding_comments: resolutionNotes,
          status: 'Band disagreement resolution completed',
          updated_at: new Date().toISOString()
        })
        .eq('employee_id', selectedRecord.user_id)
        .eq('team_member_name', selectedRecord.employee_name)
        .eq('supervisor_id', selectedRecord.supervisor_id);

      if (performanceBandError) {
        console.error('Error updating performance bands:', performanceBandError);
        toast({
          title: "Error",
          description: "Failed to update performance bands. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Band disagreement details saved successfully.",
      });

      // Trigger refetch to get updated data
      refetch();
      setSelectedRecord(null);
    } catch (error) {
      console.error('Error saving band disagreement:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    handleSaveAndClose
  };
};
